/** Prefer the panel under the pointer, then the visible panel, then the first panel. */
export function resolveCanvasDropPanelId(
  panelAtDrop: string | undefined,
  activePanel: string | undefined,
  firstPanel: string | undefined,
): string | undefined {
  return panelAtDrop || activePanel || firstPanel;
}

type NestedCanvasElement = {
  tempId: string;
  parentElementId?: string | null;
};

/** Select the deepest visible container whose rendered bounds contain the pointer. */
export function findDeepestVisibleContainerAtPosition<
  T extends NestedCanvasElement,
>(
  elements: T[],
  isContainer: (element: T) => boolean,
  isVisibleAtPosition: (element: T) => boolean,
): T | null {
  const elementsById = new Map(
    elements.map((element) => [element.tempId, element]),
  );
  let deepest: T | null = null;
  let deepestDepth = -1;

  for (const element of elements) {
    if (!isContainer(element) || !isVisibleAtPosition(element)) continue;

    let depth = 0;
    let parentId = element.parentElementId;
    const visited = new Set<string>([element.tempId]);
    while (parentId && !visited.has(parentId)) {
      visited.add(parentId);
      depth += 1;
      parentId = elementsById.get(parentId)?.parentElementId;
    }

    if (depth > deepestDepth) {
      deepest = element;
      deepestDepth = depth;
    }
  }

  return deepest;
}

export function findVisibleCanvasPanelId(
  parentId: string,
  panels: HTMLElement[],
): string | undefined {
  const matchingPanels = panels.filter(
    (panel) => panel.dataset.canvasParentId === parentId,
  );

  return matchingPanels.find((panel) => {
    if (
      panel.hasAttribute("hidden") ||
      panel.getAttribute("aria-hidden") === "true"
    )
      return false;
    const rect = panel.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return false;
    const style = window.getComputedStyle(panel);
    return style.display !== "none" && style.visibility !== "hidden";
  })?.dataset.canvasPanelId;
}
